"use client";

import { useRef } from "react";
import { ArrowRight } from "lucide-react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";
import { Link } from "@/components/common/Link";
gsap.registerPlugin(useGSAP, ScrollTrigger);
export default function Localseohero() {
  const container = useRef<HTMLElement>(null);
  const headlineWords = [{
    text: "Professional",
    italic: false
  }, {
    text: "SEO",
    italic: false
  }, {
    text: "Consultant",
    italic: false
  }, {
    text: "for",
    italic: false
  }, {
    text: "Sustainable",
    italic: true,
    marker: true
  }, {
    text: "Search",
    italic: false
  }, {
    text: "Growth",
    italic: false
  }];
  useGSAP(() => {
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.set(".word-inner", {
        y: 40
      });
      gsap.to(".word-inner", {
        opacity: 1,
        y: 0,
        duration: 0.9,
        ease: "power3.out",
        stagger: 0.07
      });
      const totalHeadline = 0.9 + 0.07 * headlineWords.length;
      gsap.to(".marker-path", {
        strokeDashoffset: 0,
        duration: 0.6,
        ease: "power2.inOut",
        delay: 0.3 + 0.07 * 3
      });
      gsap.set([".hero-kicker", ".hero-body", ".hero-meta", ".hero-email"], {
        y: 24
      });
      gsap.to([".hero-kicker", ".hero-body", ".hero-meta", ".hero-email"], {
        opacity: 1,
        y: 0,
        duration: 0.7,
        ease: "power3.out",
        delay: 0.4,
        stagger: 0.08
      });
    });
    mm.add("(prefers-reduced-motion: reduce)", () => {
      gsap.set([".word-inner", ".hero-kicker", ".hero-body", ".hero-meta", ".hero-email"], {
        opacity: 1,
        y: 0
      });
      gsap.set(".marker-path", {
        strokeDashoffset: 0
      });
    });
  }, {
    scope: container
  });
  return <section data-section-id="30678" id="localseohero" data-nav="light" ref={container} className="relative overflow-hidden bg-background text-foreground" style={{
    paddingTop: "clamp(112px, 16vh, 200px)",
    paddingBottom: "clamp(112px, 16vh, 200px)"
  }}>
      <div className="relative mx-auto w-full" style={{
      maxWidth: "1440px",
      paddingLeft: "clamp(24px, 5vw, 88px)",
      paddingRight: "clamp(24px, 5vw, 88px)"
    }}>
        <div className="grid grid-cols-12 gap-4 md:gap-6">
          {}
          <div className="col-span-12 md:col-span-4 lg:col-span-3">
            <p className="hero-kicker font-sans uppercase text-muted-foreground" style={{
            opacity: 0,
            fontSize: "clamp(0.82rem, 0.9vw, 0.92rem)",
            letterSpacing: "0.18em",
            fontWeight: 500,
            lineHeight: 1
          }}>
              Independent SEO Consultant — Yassine Essaadi
            </p>
          </div>
          <div className="hidden md:block md:col-span-8 lg:col-span-9" />
        </div>

        {}
        <div className="mt-10 md:mt-16 lg:mt-20 grid grid-cols-12">
          <h1 className="col-span-12 font-serif text-foreground" style={{
          fontSize: "clamp(2.25rem, 4.5vw, 3.65rem)",
          fontWeight: 400,
          letterSpacing: "-0.035em",
          lineHeight: 0.92
        }}>
            <span className="sr-only">Professional SEO Consultant for Sustainable Search Growth</span>
            <span aria-hidden="true" className="block">
              {headlineWords.map((w, i) => <span key={i} data-index={i} className="inline-block overflow-hidden align-baseline mr-[0.22em]">
                  <span className="word-inner inline-block relative" style={{
                opacity: 0,
                fontStyle: w.italic ? "italic" : "normal"
              }}>
                    {w.text}
                    {w.marker && <svg className="absolute left-0 right-0 pointer-events-none" style={{
                  bottom: "-0.08em",
                  width: "100%",
                  height: "0.28em"
                }} viewBox="0 0 300 20" preserveAspectRatio="none" aria-hidden="true">
                        <path className="marker-path" d="M4 12 C 60 6, 120 16, 180 10 S 280 8, 296 12" fill="none" stroke="var(--chart-2)" strokeWidth="8" strokeLinecap="round" style={{
                    strokeDasharray: 600,
                    strokeDashoffset: 600,
                    filter: "url(#roughen)"
                  }} />
                        <defs>
                          <filter id="roughen">
                            <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="2" seed="3" />
                            <feDisplacementMap in="SourceGraphic" scale="2" />
                          </filter>
                        </defs>
                      </svg>}
                  </span>
                </span>)}
            </span>
          </h1>
        </div>

        {}
        <div className="mt-12 md:mt-20 grid grid-cols-12 gap-6 md:gap-8">
          <div className="hidden lg:block lg:col-span-2" />
          <div className="col-span-12 md:col-span-8 lg:col-span-6">
            <p className="hero-body font-default text-foreground" style={{
            opacity: 0,
            fontSize: "clamp(1.25rem, 1.35vw, 1.5rem)",
            fontWeight: 400,
            letterSpacing: "-0.005em",
            lineHeight: 1.55,
            maxWidth: "58ch"
          }}>
              I provide hands-on SEO strategy for national and international businesses. You work directly with me to build sustainable search visibility and revenue.
            </p>
          </div>
          <div className="col-span-12 md:col-span-4 lg:col-span-4 flex md:justify-end md:items-end">
            <div className="hero-meta flex flex-col md:items-end gap-3" style={{
            opacity: 0
          }}>
              <span className="font-sans uppercase text-muted-foreground" style={{
              fontSize: "0.9375rem",
              letterSpacing: "0.06em",
              fontWeight: 500,
              lineHeight: 1.3
            }}>
                Direct Access · Tailored Strategy
              </span>
              <Link to="/ContactPage" className="group inline-flex items-center gap-2 font-sans text-foreground" style={{
              fontSize: "1.0625rem",
              fontWeight: 500,
              letterSpacing: "0.02em"
            }}>
                <span className="relative inline-block">
                  Book Consultation
                  <span className="absolute left-0 right-0 -bottom-1 h-px origin-left scale-x-0 transition-transform duration-[260ms] group-hover:scale-x-100" style={{
                  backgroundColor: "var(--chart-3)"
                }} />
                </span>
                <ArrowRight className="h-4 w-4 transition-transform duration-[220ms] group-hover:translate-x-1.5" aria-hidden="true" />
              </Link>
            </div>
          </div>
        </div>

        {}
        <div className="hero-email hidden lg:flex absolute left-6 xl:left-10 bottom-0 items-center" style={{
        opacity: 0
      }}>
          <span className="font-sans uppercase text-muted-foreground select-none" style={{
          writingMode: "vertical-rl",
          transform: "rotate(180deg)",
          fontSize: "0.9rem",
          letterSpacing: "0.18em",
          fontWeight: 500
        }}>
            contact@ye-consultancy.com
          </span>
        </div>
      </div>
    </section>;
}